import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CalendarClock, Check, X as XIcon, Loader2, RefreshCw, Clock,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const STATUS_LABEL = {
  pending: "Pendente",
  approved: "Aprovado",
  rejected: "Rejeitado",
};

/**
 * Admin — Pedidos de alteração de reserva.
 * Lista os pedidos submetidos pelos clientes. Admin pode aprovar
 * (aplica as alterações e notifica) ou rejeitar (com nota opcional).
 */
export default function AdminRescheduleRequests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reschedule-requests", {
        params: { status: filter },
      });
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function approve(req) {
    const changes = req.requested_changes || {};
    const parts = [];
    if (changes.scheduled_date) parts.push(`data: ${changes.scheduled_date}`);
    if (changes.scheduled_time) parts.push(`hora: ${changes.scheduled_time}`);
    if (changes.notes) parts.push(`notas`);
    const msg = `Aprovar pedido? Será aplicado:\n• ${parts.join("\n• ")}\n\nO cliente será notificado.`;
    if (!window.confirm(msg)) return;
    const note = window.prompt("Nota opcional para o cliente:", "") || undefined;
    setBusyId(req.id);
    try {
      await api.post(`/admin/reschedule-requests/${req.id}/approve`, { admin_note: note });
      toast.success("Pedido aprovado. Cliente notificado.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  }

  async function reject(req) {
    const note = window.prompt(
      "Motivo da rejeição (o cliente vai ver esta mensagem):", "",
    );
    if (note === null) return;
    if (!window.confirm("Confirmar rejeição deste pedido?")) return;
    setBusyId(req.id);
    try {
      await api.post(`/admin/reschedule-requests/${req.id}/reject`, { admin_note: note || null });
      toast.success("Pedido rejeitado. Cliente notificado.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  }

  const filters = useMemo(() => [
    { key: "pending", label: "Pendentes" },
    { key: "approved", label: "Aprovados" },
    { key: "rejected", label: "Rejeitados" },
    { key: "all", label: "Todos" },
  ], []);

  return (
    <div className="space-y-8" data-testid="admin-reschedule-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-[#d4af37] flex items-center gap-2">
            <CalendarClock size={14} /> Admin · Alterações
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Pedidos de alteração</h1>
          <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
            Reservas que os clientes querem remarcar. Aprove apenas se
            houver disponibilidade da equipa — o pedido aplica as
            alterações de imediato e notifica o cliente.
          </p>
        </div>
        <button onClick={load}
                className="px-3 py-2 text-xs uppercase tracking-widest border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] inline-flex items-center gap-2"
                data-testid="reschedule-refresh">
          <RefreshCw size={12} /> Recarregar
        </button>
      </header>

      <div className="flex flex-wrap gap-2">
        {filters.map(f => {
          const active = filter === f.key;
          return (
            <button key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm ${
                      active
                        ? "border-[#d4af37] text-[#d4af37] bg-[#062c27]"
                        : "border-[#1a4640] text-[#a0b2af] hover:text-[#fdfbf7] hover:border-[#d4af37]/50"
                    }`}
                    data-testid={`reschedule-filter-${f.key}`}>
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-sm text-[#a0b2af]">A carregar…</div>
      ) : items.length === 0 ? (
        <div className="lux-card p-10 text-center text-sm text-[#a0b2af]"
             data-testid="reschedule-empty">
          Sem pedidos {filter === "all" ? "registados" : `com estado "${STATUS_LABEL[filter] || filter}"`}.
        </div>
      ) : (
        <div className="space-y-3" data-testid="reschedule-list">
          {items.map(req => (
            <ReqCard key={req.id} req={req} busy={busyId === req.id}
                     onApprove={() => approve(req)}
                     onReject={() => reject(req)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReqCard({ req, busy, onApprove, onReject }) {
  const cur = req.current || {};
  const ch = req.requested_changes || {};
  const statusCls = {
    pending: "text-[#f59e0b] border-[#f59e0b]/40 bg-[#f59e0b]/5",
    approved: "text-[#10b981] border-[#10b981]/40 bg-[#10b981]/5",
    rejected: "text-[#a0b2af] border-[#1a4640]",
  }[req.status] || "text-[#a0b2af] border-[#1a4640]";
  return (
    <article className="lux-card p-5 space-y-4"
             data-testid={`reschedule-card-${req.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-[#fdfbf7] font-semibold">{req.client_name}</div>
          <div className="text-[11px] text-[#a0b2af] flex items-center gap-2 mt-0.5">
            <Clock size={10} />
            {new Date(req.created_at).toLocaleString("pt-PT", {
              day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
            })}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${statusCls}`}
              data-testid={`reschedule-status-${req.id}`}>
          {STATUS_LABEL[req.status] || req.status}
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <div className="border border-[#1a4640] p-3 bg-[#062c27]/40">
          <div className="eyebrow text-[10px] text-[#a0b2af] mb-1">Atual</div>
          <div className="font-mono text-[#fdfbf7]">
            {cur.scheduled_date} · {cur.scheduled_time}
          </div>
          {cur.notes && <div className="text-[11px] text-[#a0b2af] mt-1 italic">📝 {cur.notes}</div>}
        </div>
        <div className="border border-[#d4af37]/50 p-3 bg-[#d4af37]/5">
          <div className="eyebrow text-[10px] text-[#d4af37] mb-1">Pedido</div>
          <div className="font-mono text-[#fdfbf7]">
            {ch.scheduled_date || cur.scheduled_date} · {ch.scheduled_time || cur.scheduled_time}
          </div>
          {ch.notes && <div className="text-[11px] text-[#a0b2af] mt-1 italic">📝 {ch.notes}</div>}
        </div>
      </div>

      {req.reason && (
        <div className="text-xs text-[#a0b2af] italic">
          <span className="text-[#d4af37] uppercase tracking-widest text-[10px] mr-2">Motivo do cliente:</span>
          "{req.reason}"
        </div>
      )}

      {req.admin_note && (
        <div className="text-xs text-[#a0b2af]">
          <span className="text-[#d4af37] uppercase tracking-widest text-[10px] mr-2">Nota admin:</span>
          {req.admin_note}
        </div>
      )}

      {req.status === "pending" && (
        <div className="flex items-center gap-2 pt-2 border-t border-[#1a4640]">
          <button onClick={onApprove} disabled={busy}
                  className="btn-gold text-xs px-4 py-2 uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid={`reschedule-approve-${req.id}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            Aprovar
          </button>
          <button onClick={onReject} disabled={busy}
                  className="text-xs text-[#fca5a5] border border-[#ef4444]/50 px-4 py-2 uppercase tracking-widest inline-flex items-center gap-2 hover:bg-[#ef4444]/10 disabled:opacity-50"
                  data-testid={`reschedule-reject-${req.id}`}>
            <XIcon size={12} /> Rejeitar
          </button>
        </div>
      )}

      {req.decided_at && (
        <div className="text-[10px] text-[#a0b2af] pt-2 border-t border-[#1a4640]">
          Decidido em {new Date(req.decided_at).toLocaleString("pt-PT")}
        </div>
      )}
    </article>
  );
}
